import config from "../config";

const analyticsService = {
  /**
   * Get top converting products
   * @param {string} clientId - Client ID
   * @param {number} limit - Number of products to return (default: 10)
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} Top converting products response
   */
  getTopConvertingProducts: async (clientId, limit = 10, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/product-performance/top-converting-products?client_id=${clientId}&limit=${limit}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching top converting products from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Top converting products fetched successfully");
      } else {
        console.error("❌ Top converting products fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching top converting products:", error);
      throw error;
    }
  },

  /**
   * Get high clicks low conversions products
   * @param {string} clientId - Client ID
   * @param {number} limit - Number of products to return (default: 10)
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} High clicks low conversions response
   */
  getHighClicksLowConversions: async (clientId, limit = 10, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/product-performance/high-clicks-low-conversions?client_id=${clientId}&limit=${limit}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching high clicks low conversions from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ High clicks low conversions fetched successfully");
      } else {
        console.error("❌ High clicks low conversions fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching high clicks low conversions:", error);
      throw error;
    }
  },

  /**
   * Get most recommended categories
   * @param {string} clientId - Client ID
   * @param {number} limit - Number of categories to return (default: 10)
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} Most recommended categories response
   */
  getMostRecommendedCategories: async (clientId, limit = 10, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/product-performance/most-recommended-categories?client_id=${clientId}&limit=${limit}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching most recommended categories from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Most recommended categories fetched successfully");
      } else {
        console.error("❌ Most recommended categories fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching most recommended categories:", error);
      throw error;
    }
  },

  /**
   * Get revenue driven by AI
   * @param {string} clientId - Client ID
   * @param {number} days - Number of days to look back (default: 30)
   * @returns {Promise} Revenue driven by AI response
   */
  getRevenueDrivenByAI: async (clientId, days = 30) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      const url = `${config.API_BASE_URL}/analytics/sales-conversion/revenue-driven-by-ai?client_id=${clientId}&days=${days}`;

      console.log(`🔍 Fetching revenue driven by AI from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Revenue driven by AI fetched successfully");
      } else {
        console.error("❌ Revenue driven by AI fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching revenue driven by AI:", error);
      throw error;
    }
  },

  /**
   * Get product recommendation funnel
   * @param {string} clientId - Client ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} Product recommendation funnel response
   */
  getProductRecommendationFunnel: async (clientId, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/sales-conversion/product-recommendation-funnel?client_id=${clientId}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching product recommendation funnel from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Product recommendation funnel fetched successfully");
      } else {
        console.error("❌ Product recommendation funnel fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching product recommendation funnel:", error);
      throw error;
    }
  },

  /**
   * Get mood trends
   * @param {string} clientId - Client ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} Mood trends response
   */
  getMoodTrends: async (clientId, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/mood-trends/trends?client_id=${clientId}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching mood trends from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Mood trends fetched successfully");
      } else {
        console.error("❌ Mood trends fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching mood trends:", error);
      throw error;
    }
  },

  /**
   * Get conversation analysis
   * @param {string} clientId - Client ID
   * @returns {Promise} Conversation analysis response
   */
  getConversationAnalysis: async (clientId) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      const url = `${config.API_BASE_URL}/analytics/conversation-analysis/metrics?client_id=${clientId}`;

      console.log(`🔍 Fetching conversation analysis from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Conversation analysis fetched successfully");
      } else {
        console.error("❌ Conversation analysis fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching conversation analysis:", error);
      throw error;
    }
  },

  /**
   * Get engagement timing
   * @param {string} clientId - Client ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} Engagement timing response
   */
  getEngagementTiming: async (clientId, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/engagement-timing/by-hour?client_id=${clientId}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching engagement timing from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Engagement timing fetched successfully");
      } else {
        console.error("❌ Engagement timing fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching engagement timing:", error);
      throw error;
    }
  },

  /**
   * Get drop-off keyword insights
   * @param {string} clientId - Client ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} Drop-off keyword insights response
   */
  getDropoffKeywordInsights: async (clientId, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/conversation-analysis/dropoff-keyword-insights?client_id=${clientId}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching drop-off keyword insights from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Drop-off keyword insights fetched successfully");
      } else {
        console.error("❌ Drop-off keyword insights fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching drop-off keyword insights:", error);
      throw error;
    }
  },

  /**
   * Get most repeated user queries
   * @param {string} clientId - Client ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} Most repeated user queries response
   */
  getMostRepeatedUserQueries: async (clientId, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/conversation-analysis/most-repeated-queries?client_id=${clientId}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching most repeated user queries from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ Most repeated user queries fetched successfully");
      } else {
        console.error("❌ Most repeated user queries fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching most repeated user queries:", error);
      throw error;
    }
  },

  /**
   * Get user acquisition and retention trends
   * @param {string} clientId - Client ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise} User acquisition and retention trends response
   */
  getUserAcquisitionRetentionTrends: async (clientId, startDate = null, endDate = null) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      let url = `${config.API_BASE_URL}/analytics/conversation-analysis/user-acquisition-retention?client_id=${clientId}`;

      if (startDate) {
        url += `&start_date=${startDate}`;
      }
      if (endDate) {
        url += `&end_date=${endDate}`;
      }

      console.log(`🔍 Fetching user acquisition and retention trends from: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: config.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        console.log("✅ User acquisition and retention trends fetched successfully");
      } else {
        console.error("❌ User acquisition and retention trends fetch failed:", data.message);
      }

      return data;
    } catch (error) {
      console.error("❌ Error fetching user acquisition and retention trends:", error);
      throw error;
    }
  },
};

export default analyticsService;

